import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { requestLinkUpdate, deleteDeal, fetchDealMarket } from '../lib/api'

// Deal type accent colors
const TYPE_ACCENT = {
  acquisition: { color: '#3B82F6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)' },
  development: { color: '#2ECC71', bg: 'rgba(46,204,113,0.12)', border: 'rgba(46,204,113,0.3)' },
  lending:     { color: '#D4AF37', bg: 'rgba(212,175,55,0.12)', border: 'rgba(212,175,55,0.3)' },
}
const STATUS_ACCENT = {
  won:     { color: '#2ECC71', bg: 'rgba(46,204,113,0.1)',  border: 'rgba(46,204,113,0.3)' },
  lost:    { color: '#E74C3C', bg: 'rgba(231,76,60,0.1)',   border: 'rgba(231,76,60,0.3)' },
  open:    { color: '#3B82F6', bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.3)' },
  deleted: { color: '#94A3B8', bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.2)' },
}

// Fallback SharePoint parent folders per deal_category, used when the deal's
// link_main_folder was imported as a bare filename/label instead of a URL
// (legacy Excel import stripped the hyperlink target). Points users at the
// site's top-level category folder so they can search from there.
const CATEGORY_PARENT_FOLDER = {
  acquisition: 'https://povernisheikh.sharepoint.com/sites/PortRichey/Shared%20Documents/%5BAcquisition%5D%20-%20Potential%20Underwriting',
  development: 'https://povernisheikh.sharepoint.com/sites/PSGDevelopment/Shared%20Documents/Development%20-%20Potential%20Underwriting',
  lending:     'https://povernisheikh.sharepoint.com/sites/Credit-PSGTeam/Shared%20Documents/Potential%20Loans',
}

// SharePoint link fields — keys match the v_deals API response (snake_case).
const LINK_FIELDS = [
  { key: 'link_main_folder',    label: 'Main Deal Folder',      icon: '📁' },
  { key: 'link_om',             label: 'Offering Memorandum',   icon: '📄' },
  { key: 'link_uw_model',       label: 'Underwriting Model',    icon: '📊' },
  { key: 'link_due_diligence',  label: 'Due Diligence',         icon: '🔍' },
  { key: 'link_financials',     label: 'Financial Statements',  icon: '📈' },
  { key: 'link_reit_reports',   label: 'REIT Reports',          icon: '🏦' },
  { key: 'link_sources_uses',   label: 'Sources & Uses',        icon: '🏗' },
  { key: 'link_third_party_uw', label: '3rd Party UW',          icon: '🔬' },
]

function fmt(n, prefix = '$') {
  if (n == null || n === 0) return null
  if (n >= 1e9) return `${prefix}${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `${prefix}${(n / 1e6).toFixed(2)}M`
  if (n >= 1e3) return `${prefix}${(n / 1e3).toFixed(0)}K`
  return `${prefix}${n}`
}
function fmtPct(n) {
  if (n == null || n === 0) return null
  return n < 1 ? `${(n * 100).toFixed(1)}%` : `${n.toFixed(1)}%`
}

function Badge({ label, color, bg, border }) {
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 12, fontSize: 10, fontWeight: 600,
      letterSpacing: '0.06em', textTransform: 'uppercase',
      color, background: bg, border: `1px solid ${border}`,
    }}>{label}</span>
  )
}

function Row({ label, value }) {
  if (!value) return null
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
    }}>
      <span style={{ fontSize: 11, color: '#94A3B8', flexShrink: 0, marginRight: 8 }}>{label}</span>
      <span style={{ fontSize: 11, color: '#FFFFFF', textAlign: 'right' }}>{value}</span>
    </div>
  )
}

function SectionTitle({ children }) {
  return (
    <div style={{
      fontSize: 9, fontWeight: 600, color: '#94A3B8', letterSpacing: '0.12em',
      textTransform: 'uppercase', marginBottom: 6, marginTop: 2,
    }}>{children}</div>
  )
}

// ── Market snapshot section (Day-2 context) ──────────────────────────────
// Lazy-loaded on first expand. Backend caches with 7-day TTL so expanding the
// same deal twice in a week is one DB read, no API calls.
function MarketSection({ dealId }) {
  const [expanded, setExpanded] = useState(false)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['deal-market', dealId],
    queryFn:  ({ signal }) => fetchDealMarket(dealId, { signal }),
    enabled:  expanded,
    staleTime: 1000 * 60 * 60,  // 1h browser cache
    retry: 1,
  })

  const headerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 24, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer', userSelect: 'none',
  }
  const labelStyle = {
    fontSize: 9, fontWeight: 600, color: '#94A3B8', letterSpacing: '0.12em',
    textTransform: 'uppercase',
  }
  const rowStyle = {
    display: 'grid', gridTemplateColumns: '1fr auto', gap: 8,
    padding: '6px 0', fontSize: 11, color: '#CBD5E1',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  }
  const trend = (pct) => {
    if (pct == null) return <span style={{ color: '#64748B' }}>—</span>
    const up = pct >= 0
    return (
      <span style={{ color: up ? '#2ECC71' : '#E74C3C', fontWeight: 600 }}>
        {up ? '▲' : '▼'} {Math.abs(pct).toFixed(2)}%
      </span>
    )
  }
  const fmt = (v, prefix = '', suffix = '') =>
    v == null ? <span style={{ color: '#64748B' }}>—</span> : <span>{prefix}{v}{suffix}</span>

  return (
    <>
      <div style={headerStyle} onClick={() => setExpanded(e => !e)}>
        <div style={labelStyle}>📈 Market snapshot</div>
        <div style={{ fontSize: 10, color: '#94A3B8' }}>{expanded ? '▲' : '▼'}</div>
      </div>
      {expanded && (
        <div style={{ marginTop: 8 }}>
          {isLoading && <div style={{ color: '#94A3B8', fontSize: 11, padding: '8px 0' }}>Loading market data…</div>}
          {isError   && <div style={{ color: '#E74C3C', fontSize: 11, padding: '8px 0' }}>Could not load market data.</div>}
          {data && (
            <>
              {/* Housing (FRED) */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, color: '#D4AF37', letterSpacing: '0.08em', marginBottom: 4 }}>
                  HOUSING · FRED
                </div>
                <div style={rowStyle}>
                  <span>State HPI YoY</span>{trend(data.state_hpi_yoy_pct)}
                </div>
                {data.msa_hpi != null && (
                  <div style={rowStyle}>
                    <span>MSA HPI YoY</span>{trend(data.msa_hpi_yoy_pct)}
                  </div>
                )}
                <div style={rowStyle}>
                  <span>State median HHI</span>
                  {fmt(data.state_hhi && `$${(data.state_hhi / 1000).toFixed(1)}K`)}
                </div>
                <div style={rowStyle}>
                  <span>State HHI YoY</span>{trend(data.state_hhi_yoy_pct)}
                </div>
                {data.msa_hpi_since_entry_pct != null && (
                  <div style={rowStyle}>
                    <span>MSA HPI since entry</span>{trend(data.msa_hpi_since_entry_pct)}
                  </div>
                )}
              </div>

              {/* Demographics + Market Rate (StorTrack) */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, color: '#D4AF37', letterSpacing: '0.08em', marginBottom: 4 }}>
                  DEMOGRAPHICS · POPULATION · RATE
                </div>
                {(data.population != null) ? (
                  <>
                    <div style={rowStyle}>
                      <span>Population (5-mi)</span>
                      {fmt(data.population?.toLocaleString())}
                    </div>
                    <div style={rowStyle}>
                      <span>Projected pop growth</span>{trend(data.pop_growth_forecast_pct)}
                    </div>
                    <div style={rowStyle}>
                      <span>Rentership</span>
                      {fmt(data.rentership_pct?.toFixed?.(1), '', '%')}
                    </div>
                    <div style={rowStyle}>
                      <span>Median HHI</span>
                      {fmt(data.median_hh_income && `$${(data.median_hh_income / 1000).toFixed(1)}K`)}
                    </div>
                    <div style={rowStyle}>
                      <span>10×10 Reg rate</span>
                      {fmt(data.mkt_rate_10x10 && `$${data.mkt_rate_10x10.toFixed(0)}/mo`)}
                    </div>
                    <div style={rowStyle}>
                      <span>CC premium (per-sqft)</span>
                      {fmt(data.mkt_cc_premium_pct?.toFixed?.(1), '+', '%')}
                    </div>
                    <div style={rowStyle}>
                      <span>Market rate trend (12mo)</span>{trend(data.mkt_rate_trend_12mo_pct)}
                    </div>
                    <div style={rowStyle}>
                      <span>Competitors (5-mi)</span>
                      {fmt(data.mkt_store_count)}
                    </div>
                  </>
                ) : data.sources?.stortrack?.ok === false ? (
                  <div style={{ color: '#64748B', fontSize: 10, padding: '6px 0', fontStyle: 'italic' }}>
                    StorTrack credentials not configured — population & market rate pending.
                  </div>
                ) : (
                  <div style={{ color: '#64748B', fontSize: 10, padding: '6px 0', fontStyle: 'italic' }}>
                    StorTrack returned no data for this location.
                  </div>
                )}
              </div>

              <div style={{ fontSize: 9, color: '#64748B', marginTop: 6 }}>
                Snapshot: {data.snapshot_date}
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}


export default function DealSidebar({ deal, canDelete, onClose }) {
  const queryClient = useQueryClient()
  const [editingLinks, setEditingLinks] = useState(false)
  const [draftLinks, setDraftLinks]     = useState({})
  const [saving, setSaving]             = useState(false)
  const [banner, setBanner]             = useState(null)  // {type:'ok'|'err', text}
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting]         = useState(false)

  const ta = TYPE_ACCENT[deal.type]   || TYPE_ACCENT.acquisition
  const sa = STATUS_ACCENT[deal.status?.toLowerCase()] || STATUS_ACCENT.open

  // Read link_* directly from the deal (from v_deals). No client-side overrides —
  // additions go through the intake queue and are only visible once an
  // Editor/Admin approves the change.
  const currentLinks = {}
  for (const { key } of LINK_FIELDS) currentLinks[key] = deal[key] || ''
  const hasLinks = LINK_FIELDS.some(f => currentLinks[f.key])

  function startEdit() {
    setDraftLinks({ ...currentLinks })
    setEditingLinks(true)
    setBanner(null)
  }

  async function saveLinks() {
    // Compute only the diff — no need to submit unchanged fields
    const changes = {}
    for (const { key } of LINK_FIELDS) {
      const newVal = (draftLinks[key] || '').trim()
      const oldVal = currentLinks[key] || ''
      if (newVal !== oldVal) changes[key] = newVal   // empty string = explicit clear
    }
    if (Object.keys(changes).length === 0) {
      setEditingLinks(false)
      return
    }
    setSaving(true)
    setBanner(null)
    try {
      const result = await requestLinkUpdate(deal.id, changes, null)
      setBanner({
        type: 'ok',
        text: `Submitted for review (${result.intake_id?.slice(0, 8)}...). An admin will approve before it lands on this deal.`,
      })
      setEditingLinks(false)
    } catch (err) {
      setBanner({ type: 'err', text: `Failed: ${err.message}` })
    } finally {
      setSaving(false)
    }
  }

  function cancelEdit() {
    setEditingLinks(false)
    setBanner(null)
  }

  async function performDelete() {
    setDeleting(true)
    setBanner(null)
    try {
      await deleteDeal(deal.id)
      // Refresh the deal list so the pin disappears from the map + the sidebar closes.
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      // Fire onClose after a moment so the user sees the confirmation
      setTimeout(() => onClose && onClose(), 400)
    } catch (err) {
      setBanner({ type: 'err', text: `Delete failed: ${err.message}` })
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(deal.address || deal.name)}`

  return (
    <aside style={{
      width: 312, flexShrink: 0, display: 'flex', flexDirection: 'column',
      background: '#061020',
      borderLeft: '1px solid rgba(212,175,55,0.18)',
      overflow: 'hidden',
    }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{
        padding: '14px 16px 12px',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        background: '#040e1a',
      }}>
        {/* Close + type accent bar */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Accent line */}
            <div style={{
              width: 28, height: 2, borderRadius: 2, background: ta.color,
              marginBottom: 8,
            }} />
            <h2 style={{
              fontSize: 13, fontWeight: 700, color: '#FFFFFF',
              lineHeight: 1.35, margin: 0, wordBreak: 'break-word',
            }}>{deal.name || '(Unnamed Deal)'}</h2>
            <p style={{ fontSize: 11, color: '#94A3B8', marginTop: 3, marginBottom: 0 }}>
              {deal.org || deal.contact || '–'}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#94A3B8', fontSize: 18, lineHeight: 1, padding: '0 0 0 4px',
              flexShrink: 0,
            }}
          >×</button>
        </div>

        {/* Badges */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          <Badge label={deal.type} {...ta} />
          {deal.status && <Badge label={deal.status} {...sa} />}
          {deal.propertyType && (
            <Badge label={deal.propertyType}
              color="#94A3B8" bg="rgba(148,163,184,0.08)" border="rgba(148,163,184,0.2)" />
          )}
          {deal.locSource === 'state' && (
            <Badge label="approx. location"
              color="#D4AF37" bg="rgba(212,175,55,0.1)" border="rgba(212,175,55,0.3)" />
          )}
        </div>
      </div>

      {/* ── Scrollable body ─────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>

        {/* Deal Info */}
        <SectionTitle>Deal Info</SectionTitle>
        <Row label="Value"        value={fmt(deal.value)} />
        <Row label="Stage"        value={deal.stage} />
        <Row label="Pipeline"     value={deal.pipeline} />
        <Row label="Owner"        value={deal.owner} />
        <Row label="Date Sourced" value={deal.date_sourced} />
        {deal.won_date  && <Row label="Won"  value={deal.won_date} />}
        {deal.lost_date && <Row label="Lost" value={deal.lost_date} />}
        {deal.lostReason && <Row label="Lost Reason" value={deal.lostReason} />}

        {/* Location */}
        <div style={{ marginTop: 14 }}>
          <SectionTitle>Location</SectionTitle>
          <Row label="City"  value={deal.city} />
          <Row label="State" value={deal.state} />
          <Row label="ZIP"   value={deal.zip} />
          {deal.address && (
            <a href={mapsUrl} target="_blank" rel="noreferrer"
               style={{
                 display: 'block', marginTop: 6, fontSize: 11,
                 color: '#3B82F6', textDecoration: 'none',
                 overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
               }}>
              📍 {deal.address}
            </a>
          )}
        </div>

        {/* Property / Loan Metrics */}
        {(deal.type === 'acquisition' || deal.type === 'development') && (
          <div style={{ marginTop: 14 }}>
            <SectionTitle>Property Metrics</SectionTitle>
            <Row label="NRSF"         value={deal.nrsf ? `${deal.nrsf.toLocaleString()} SF` : null} />
            <Row label="Price / SF"   value={deal.pricePerFoot ? `$${deal.pricePerFoot.toFixed(2)}` : null} />
            <Row label="Wtd Avg Rent" value={deal.weightedAvgRent ? `$${deal.weightedAvgRent.toFixed(2)}` : null} />
            <Row label="Year Built"   value={deal.yearBuilt} />
            <Row label="Deal Type"    value={deal.dealType} />
          </div>
        )}

        {deal.type === 'lending' && (
          <div style={{ marginTop: 14 }}>
            <SectionTitle>Loan Metrics</SectionTitle>
            <Row label="Loan Type"     value={deal.loanType} />
            <Row label="LTV"           value={fmtPct(deal.ltv)} />
            <Row label="LTC"           value={fmtPct(deal.ltc)} />
            <Row label="Debt Yield"    value={fmtPct(deal.debtYield)} />
            <Row label="Rate Spread"   value={deal.interestRate ? `${deal.interestRate}%` : null} />
            <Row label="Term"          value={deal.term} />
            <Row label="NRSF"          value={deal.nrsf ? `${deal.nrsf.toLocaleString()} SF` : null} />
            <Row label="Appraised Val" value={fmt(deal.appraisedValue)} />
            <Row label="UW Sizing"     value={fmt(deal.uwSizing)} />
            <Row label="Recourse"      value={deal.recourse} />
            {deal.notes && (
              <div style={{
                marginTop: 8, padding: '8px 10px', borderRadius: 6,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                fontSize: 11, color: '#94A3B8', lineHeight: 1.5,
              }}>{deal.notes}</div>
            )}
          </div>
        )}

        {/* ── SharePoint Links ────────────────────────────────────────── */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <SectionTitle>Folder Links</SectionTitle>
            {hasLinks && !editingLinks && (
              <button onClick={startEdit} style={{
                fontSize: 9, color: '#D4AF37', background: 'none', border: 'none',
                cursor: 'pointer', letterSpacing: '0.08em', padding: 0, marginTop: -2,
              }}>EDIT</button>
            )}
          </div>

          {banner && (
            <div style={{
              padding: '8px 10px', borderRadius: 5, fontSize: 11, marginBottom: 8,
              background: banner.type === 'ok' ? 'rgba(46,204,113,0.1)' : 'rgba(231,76,60,0.1)',
              border: `1px solid ${banner.type === 'ok' ? '#2ECC71' : '#E74C3C'}`,
              color: banner.type === 'ok' ? '#2ECC71' : '#E74C3C',
              lineHeight: 1.4,
            }}>{banner.text}</div>
          )}

          {!editingLinks ? (
            <>
              {LINK_FIELDS.map(({ key, label, icon }) => {
                const url = currentLinks[key]
                if (!url) return null
                // Legacy Excel import left some columns with bare filenames or
                // labels — rendering those as <a href> makes the browser treat
                // them as relative paths and the SPA falls back to index.html.
                const isUrl = /^https?:\/\//i.test(url)
                if (!isUrl) {
                  const parentUrl = key === 'link_main_folder'
                    ? CATEGORY_PARENT_FOLDER[deal.deal_category]
                    : null
                  return (
                    <div key={key} title={url}
                         style={{
                           display: 'flex', flexDirection: 'column', gap: 4,
                           padding: '7px 10px', marginBottom: 4, borderRadius: 6,
                           background: 'rgba(148,163,184,0.06)',
                           border: '1px dashed rgba(148,163,184,0.25)',
                         }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, opacity: 0.6 }}>{icon}</span>
                        <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500 }}>{label}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 9, color: '#94A3B8', fontStyle: 'italic' }}>filename only — no URL</span>
                      </div>
                      {parentUrl && (
                        <a href={parentUrl} target="_blank" rel="noreferrer"
                           style={{ fontSize: 10, color: '#D4AF37', textDecoration: 'none', paddingLeft: 21 }}>
                          Browse {deal.deal_category} folder in SharePoint ↗
                        </a>
                      )}
                    </div>
                  )
                }
                return (
                  <a key={key} href={url} target="_blank" rel="noreferrer"
                     style={{
                       display: 'flex', alignItems: 'center', gap: 8,
                       padding: '7px 10px', marginBottom: 4, borderRadius: 6,
                       background: 'rgba(212,175,55,0.06)',
                       border: '1px solid rgba(212,175,55,0.15)',
                       textDecoration: 'none', transition: 'background 0.1s',
                     }}
                     onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.12)'}
                     onMouseLeave={e => e.currentTarget.style.background = 'rgba(212,175,55,0.06)'}
                  >
                    <span style={{ fontSize: 13 }}>{icon}</span>
                    <span style={{ fontSize: 11, color: '#FFFFFF', fontWeight: 500 }}>{label}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#D4AF37', opacity: 0.7 }}>↗</span>
                  </a>
                )
              })}

              {!hasLinks && (
                <div style={{ fontSize: 11, color: '#94A3B8', fontStyle: 'italic', marginBottom: 8 }}>
                  No folder links for this deal yet.
                </div>
              )}

              <button onClick={startEdit} style={{
                marginTop: 4, width: '100%', padding: '7px 0', borderRadius: 6,
                background: 'none',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#94A3B8', fontSize: 11, cursor: 'pointer', letterSpacing: '0.04em',
                transition: 'border-color 0.15s, color 0.15s',
              }}
                onMouseEnter={e => { e.target.style.borderColor = 'rgba(212,175,55,0.4)'; e.target.style.color = '#D4AF37' }}
                onMouseLeave={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.color = '#94A3B8' }}
              >
                {hasLinks ? '+ Propose Link Changes' : '+ Propose Folder Links'}
              </button>
            </>
          ) : (
            <div>
              <div style={{ fontSize: 10, color: '#D4AF37', marginBottom: 8, letterSpacing: '0.04em' }}>
                Changes route to the Review Queue for admin approval before taking effect.
              </div>
              {LINK_FIELDS.map(({ key, label }) => (
                <div key={key} style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 10, color: '#94A3B8', display: 'block', marginBottom: 4, letterSpacing: '0.05em' }}>
                    {label}
                  </label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={draftLinks[key] || ''}
                    onChange={e => setDraftLinks(d => ({ ...d, [key]: e.target.value }))}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      padding: '6px 10px',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 5, color: '#FFFFFF', fontSize: 11, outline: 'none',
                    }}
                  />
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button
                  onClick={saveLinks}
                  disabled={saving}
                  style={{
                    flex: 1, padding: '8px 0', borderRadius: 6, border: 'none',
                    cursor: saving ? 'wait' : 'pointer',
                    background: saving ? 'rgba(212,175,55,0.5)' : '#D4AF37',
                    color: '#0A192F', fontWeight: 700, fontSize: 11, letterSpacing: '0.05em',
                  }}
                >{saving ? 'SUBMITTING…' : 'SUBMIT FOR REVIEW'}</button>
                <button onClick={cancelEdit} disabled={saving} style={{
                  flex: 1, padding: '8px 0', borderRadius: 6, cursor: 'pointer',
                  background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#94A3B8', fontSize: 11,
                }}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* ── Day-2 Market snapshot (StorTrack + FRED) ──────────────────── */}
        <MarketSection dealId={deal.id} />

        {/* ── Danger zone: soft-delete (Editor/Admin only) ─────────────── */}
        {canDelete && (
          <div style={{ marginTop: 24, paddingTop: 14, borderTop: '1px solid rgba(231,76,60,0.15)' }}>
            <div style={{
              fontSize: 9, fontWeight: 600, color: '#E74C3C', letterSpacing: '0.12em',
              textTransform: 'uppercase', marginBottom: 8,
            }}>Danger zone</div>
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                style={{
                  width: '100%', padding: '8px 0', borderRadius: 6, cursor: 'pointer',
                  background: 'none',
                  border: '1px solid rgba(231,76,60,0.3)',
                  color: '#E74C3C', fontSize: 11, letterSpacing: '0.05em',
                }}
              >Delete Deal</button>
            ) : (
              <div>
                <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 8, lineHeight: 1.4 }}>
                  Soft-delete <span style={{ color: '#FFFFFF' }}>{deal.name}</span>?
                  It will disappear from the map. The row is preserved in the DB
                  and can be undone by an admin.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={performDelete}
                    disabled={deleting}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 6, border: 'none',
                      cursor: deleting ? 'wait' : 'pointer',
                      background: deleting ? 'rgba(231,76,60,0.5)' : '#E74C3C',
                      color: '#FFFFFF', fontWeight: 700, fontSize: 11, letterSpacing: '0.05em',
                    }}
                  >{deleting ? 'DELETING…' : 'YES, DELETE'}</button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleting}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 6, cursor: 'pointer',
                      background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                      color: '#94A3B8', fontSize: 11,
                    }}
                  >Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </aside>
  )
}
